import { Button } from '@ipfabric/ui-components/Button/Button';
import { Dialog } from '@ipfabric/ui-components/Dialog/Dialog';
import { Step, type StepProps } from 'components/SteppedDialog/Step';
import {
  Children,
  type FC,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useState,
} from 'react';

export type SteppedDialogProps = {
  children: ReactNode;
  onClose: () => void;
  /**
   * Called when the primary button is pressed on the last step. Falls back to `onClose` when omitted.
   */
  onComplete?: () => void;
};

type StepFooterProps = {
  currentStep: number;
  stepCount: number;
  nextLabel: string;
  onBack: () => void;
  onNext: () => void;
};

const StepFooter: FC<StepFooterProps> = ({
  currentStep,
  stepCount,
  nextLabel,
  onBack,
  onNext,
}) => {
  const isFirstStep = currentStep === 0;

  return (
    <div className='flex w-full items-center gap-xs'>
      <Button
        className={`flex-1${isFirstStep ? ' invisible' : ''}`}
        variant='outline'
        onClick={onBack}
        disabled={isFirstStep}
        aria-hidden={isFirstStep}
      >
        Back
      </Button>
      <div
        className='flex flex-1 items-center justify-center gap-3xs'
        data-testid='stepped-dialog-dots'
      >
        {Array.from({ length: stepCount }, (_, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: dots are static and purely positional
            key={index}
            className={
              index === currentStep
                ? 'h-3 w-5 rounded-full bg-brand'
                : 'size-3 rounded-full bg-shade-10'
            }
          />
        ))}
      </div>
      <Button className='flex-1' variant='primary' onClick={onNext}>
        {nextLabel}
      </Button>
    </div>
  );
};

export const SteppedDialog: FC<SteppedDialogProps> = ({
  children,
  onClose,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = Children.toArray(children).filter(
    (child): child is ReactElement<StepProps> =>
      isValidElement(child) && child.type === Step,
  );

  const activeStep = steps[currentStep];
  if (!activeStep) {
    return null;
  }

  const active = activeStep.props;
  const isLastStep = currentStep === steps.length - 1;

  const handleBack = () => setCurrentStep((step) => Math.max(0, step - 1));
  const handleNext = () => {
    if (isLastStep) {
      (onComplete ?? onClose)();
      return;
    }
    setCurrentStep((step) => step + 1);
  };

  return (
    <Dialog
      size='large'
      closeOnBackdropClick={false}
      onClose={onClose}
      header={{
        title: active.title,
        withoutCloseDecoration: active.showClose ? undefined : true,
      }}
      footer={
        <StepFooter
          currentStep={currentStep}
          stepCount={steps.length}
          nextLabel={active.nextLabel ?? 'Next'}
          onBack={handleBack}
          onNext={handleNext}
        />
      }
    >
      {active.children}
    </Dialog>
  );
};
